import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { AutocompleteRenderInputParams } from "@material-ui/lab/Autocomplete";
import TextFieldBase from "@material-ui/core/TextField";
import { useMouseFocusAndBlur } from "util/hooks/useMouseFocus";
import { joinClassNames } from "themes/helpers";
import styles from "./TextField.module.scss";

interface TextFieldProps extends AutocompleteRenderInputParams {
  hasError?: boolean;
  isExpanded?: boolean;
  placeholder?: string;
}

export interface TextFieldRef {
  focus: () => void;
}

export const TextField = forwardRef<TextFieldRef, TextFieldProps>(
  (
    { disabled, hasError, InputProps, isExpanded, placeholder, ...otherProps },
    ref
  ) => {
    const {
      eventHandlers,
      isFocused,
      isMouseFocused,
      wasMouseBlurred,
    } = useMouseFocusAndBlur();
    const input = useRef<any>();

    useImperativeHandle(ref, () => ({
      focus: () => {
        input.current?.focus();
      },
    }));

    return (
      <TextFieldBase
        InputProps={{
          ...InputProps,
          classes: {
            input: joinClassNames(
              styles.input,
              isExpanded ? styles.inputExpanded : styles.inputNotExpanded
            ),
            notchedOutline: joinClassNames(
              styles.notchedOutline,
              disabled && styles.notchedOutlineDisabled,
              hasError && styles.notchedOutlineError,
              isFocused &&
                !isMouseFocused &&
                styles.notchedOutlineKeyboardFocused,
              isFocused && isMouseFocused && styles.notchedOutlineMouseFocused,
              wasMouseBlurred && styles.notchedOutlineMouseBlurred
            ),
            root: joinClassNames(
              styles.root,
              isExpanded ? styles.rootExpanded : styles.rootNotExpanded,
              disabled && styles.rootDisabled,
              hasError && styles.rootError
            ),
          },
        }}
        inputRef={input}
        placeholder={placeholder}
        {...eventHandlers}
        {...otherProps}
        variant="outlined"
      />
    );
  }
);
